/* Program blocks per exercise (switching bench programs) as the Program
 * sheet edits them: setup defaults, start / end / training max edits and
 * the past blocks with their e1RM. Block maths (templates, week index,
 * targets, switching) is logic/progression; this is the app side of it, so
 * every edit still goes through switchProgram and history stays one log.
 * Pure; see programs.test.js. */
import {
  PROGRAM_TEMPLATES, activeProgram, createProgramBlock, learnIncrement, programTargets, programWeekIndex,
  suggestTrainingMax, switchProgram,
} from './logic/progression';
import { planDayFor } from './logic/schedule';
import { sessionSeries } from './logic/trends';

const isNum = v => typeof v === 'number' && Number.isFinite(v);
const isObj = v => !!v && typeof v === 'object' && !Array.isArray(v);
const posInt = v => (isNum(v) && v >= 1 ? Math.floor(v) : null);

/* One line per template for the switch list; the full text shows in setup. */
export const TEMPLATE_BLURBS = {
  531: '4-week wave of 5s, 3s and 5/3/1 with an AMRAP top set, then a deload',
  '531-bbb': '5/3/1 plus 5×10 at 50% of the training max',
  'gzclp-t1': '5×3+, then 6×2+, then 10×1+; the training max goes up after each wave',
  'smolov-jr': '3 weeks, 4 sessions a week, 6×6 up to 10×3 on your 1RM',
  linear: 'Same weight for every set; add the step when all sets hit the reps',
  double: 'Add reps in a range, then add weight and start at the bottom again',
  custom: 'Your own weeks: % of training max or kg, reps and AMRAP sets',
};

export const ROUND_CHOICES = [1, 1.25, 2, 2.5, 5];

/** The switch list: every template plus Custom, with a one-line blurb. */
export function templateChoices() {
  return [
    ...PROGRAM_TEMPLATES.map(t => ({
      id: t.id, name: t.name, blurb: TEMPLATE_BLURBS[t.id] || t.description.split('. ')[0], template: t,
    })),
    { id: 'custom', name: 'Custom', blurb: TEMPLATE_BLURBS.custom, template: null },
  ];
}

export function templateOf(id) {
  return PROGRAM_TEMPLATES.find(t => t.id === id) || null;
}

/** date + n days, YYYY-MM-DD (calendar maths in UTC, so no DST surprises). */
export function addDays(date, n) {
  const [y, m, d] = String(date).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Whether `exercise` has a done set on `date`. */
export function trainedOn(state, date, exercise) {
  const sets = state?.logs?.[date]?.[exercise];
  return Array.isArray(sets) && sets.some(s => isObj(s) && s.done);
}

/* A block that starts today must not count today's sets as its first
 * session (they were done under the old targets), so a switch made after
 * training starts tomorrow. */
export function defaultStart(state, exercise, date) {
  return trainedOn(state, date, exercise) ? addDays(date, 1) : date;
}

/** Plan days that hold this exercise (by name). */
export function planDaysWith(state, exercise) {
  return (Array.isArray(state?.plan) ? state.plan : [])
    .filter(d => isObj(d) && Array.isArray(d.exercises) && d.exercises.some(e => isObj(e) && e.name === exercise));
}

function dayNames(state, ids) {
  const plan = Array.isArray(state?.plan) ? state.plan : [];
  const names = (ids || []).map(id => plan.find(d => isObj(d) && d.id === id)?.name).filter(Boolean);
  return names.length ? names : null;
}

/**
 * The setup form of a new block, prefilled.
 * @param {object} state fitness state
 * @param {{exercise: string, date: string, templateId: string, planExercise?: object,
 *   planDayId?: string}} ctx date = the day the sheet was opened for
 * @returns {object} { templateId, name, trainingMax, e1rm, tmPct, usesTm, roundTo,
 *   tmIncrement, startDate, startWeek, advance, scope: 'day'|'all', canScope,
 *   sets, reps, repMin, repMax, startWeight, weeks }; trainingMax = tmPct of the
 *   best e1RM of the last 60 days (null without one); tmIncrement = per cycle
 *   for training-max templates, per session (the learned step) for linear and
 *   double; scope 'day' when the exercise is on more than one plan day (bench
 *   on Push A and Push B), so the other day keeps its own scheme
 */
export function setupDefaults(state, ctx) {
  const { exercise, date, templateId, planExercise: pe, planDayId } = ctx;
  const t = templateOf(templateId);
  const logs = state?.logs || {};
  const meta = state?.exerciseMeta;
  const usesTm = t ? t.usesTrainingMax : true;
  const tmPct = t && isNum(t.tmPct) ? t.tmPct : 90;
  const roundTo = 2.5;
  const probe = createProgramBlock({ exercise, template: t ? t.id : 'custom', startDate: date }, 'probe');
  const step = isNum(pe?.increment) && pe.increment > 0
    ? pe.increment
    : learnIncrement(logs, exercise, probe.tmIncrement, { exerciseMeta: meta });
  const first = t ? t.weeks[0].sets[0] : null;
  const canScope = !!planDayId && planDaysWith(state, exercise).length > 1;
  return {
    templateId: t ? t.id : 'custom',
    name: t ? t.name : 'Custom',
    usesTm,
    tmPct,
    trainingMax: usesTm ? suggestTrainingMax(logs, exercise, date, { pct: tmPct, roundTo, exerciseMeta: meta }) : null,
    e1rm: suggestTrainingMax(logs, exercise, date, { pct: 100, roundTo: 0.5, exerciseMeta: meta }),
    roundTo,
    tmIncrement: t && t.rule ? step : probe.tmIncrement,
    startDate: defaultStart(state, exercise, date),
    startWeek: 0,
    advance: 'session',
    scope: canScope ? 'day' : 'all',
    canScope,
    sets: posInt(pe?.sets) || (t ? t.weeks[0].sets.length : 3),
    reps: t && t.rule === 'linear' ? (posInt(pe?.reps) || first.reps) : null,
    repMin: t && t.rule === 'double' ? (posInt(pe?.repMin) || first.repMin) : null,
    repMax: t && t.rule === 'double' ? (posInt(pe?.repMax) || first.reps) : null,
    startWeight: null,
    weeks: t ? null : [{ label: 'Week 1', sets: [{ pct: 75, reps: 5 }, { pct: 80, reps: 5 }, { pct: 85, reps: 5, amrap: true }] }],
  };
}

/** Whether a custom block's weeks need a training max (any % set). */
export function weeksUsePct(weeks) {
  return (Array.isArray(weeks) ? weeks : []).some(w => (w?.sets || []).some(s => isNum(s?.pct) && s.pct > 0));
}

/**
 * The block a setup form describes, ready for switchProgram.
 * @param {object} form setupDefaults(...) as edited
 * @param {{exercise: string, planDayId?: string}} ctx
 * @param {string} id e.g. uid('prog')
 */
export function blockFromSetup(form, ctx, id) {
  const t = templateOf(form.templateId);
  const needsTm = t ? t.usesTrainingMax : weeksUsePct(form.weeks);
  const fields = {
    exercise: ctx.exercise,
    template: t ? t.id : 'custom',
    name: form.name,
    startDate: form.startDate,
    trainingMax: needsTm && isNum(form.trainingMax) && form.trainingMax > 0 ? form.trainingMax : null,
    roundTo: form.roundTo,
    tmIncrement: isNum(form.tmIncrement) && form.tmIncrement >= 0 ? form.tmIncrement : undefined,
    advance: form.advance,
    startWeek: form.startWeek,
    by: 'app',
  };
  if (form.scope === 'day' && ctx.planDayId) fields.planDayIds = [ctx.planDayId];
  if (t && t.rule) {
    fields.sets = form.sets;
    if (t.rule === 'linear') fields.reps = form.reps;
    else { fields.repMin = form.repMin; fields.repMax = form.repMax; }
    if (isNum(form.startWeight) && form.startWeight > 0) fields.startWeight = form.startWeight;
  }
  if (!t) fields.weeks = form.weeks;
  return createProgramBlock(fields, id);
}

/** What a setup form still needs before Start, or null when it is ready. */
export function setupProblem(form) {
  const t = templateOf(form.templateId);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.startDate || '')) return 'Pick a start date';
  if (!t) {
    if (!(form.weeks || []).some(w => (w.sets || []).length)) return 'Add at least one set';
    if (weeksUsePct(form.weeks) && !(form.trainingMax > 0)) return 'Enter a training max for the % sets';
    return null;
  }
  if (t.usesTrainingMax && !(form.trainingMax > 0)) return 'Enter a training max';
  return null;
}

/** state with the block started (the running one ends the day before). */
export function startProgram(state, block) {
  return { ...state, programs: switchProgram(state.programs, block) };
}

function replaceBlock(programs, id, patch) {
  return (Array.isArray(programs) ? programs : []).map(b => (isObj(b) && b.id === id ? { ...b, ...patch } : b));
}

/**
 * End a block so the plan progression takes over. It ends on `date` when
 * the exercise was already trained that day (today stays in the block),
 * else the day before; a block that has not started yet ends before it
 * starts, so it never runs.
 */
export function endProgram(state, block, date) {
  let end = trainedOn(state, date, block.exercise) ? date : addDays(date, -1);
  if (end < block.startDate) end = addDays(block.startDate, -1);
  return { ...state, programs: replaceBlock(state.programs, block.id, { endDate: end }) };
}

/**
 * Cancel a block that starts later: it never runs, and the block it
 * replaced (switchProgram ended it the day before) runs on again.
 */
export function cancelUpcoming(state, block) {
  const end = addDays(block.startDate, -1);
  let programs = replaceBlock(state.programs, block.id, { endDate: end });
  const prev = programs.find(b => isObj(b) && b.id !== block.id && b.exercise === block.exercise
    && b.endDate === end && b.startDate <= end);
  if (prev) programs = replaceBlock(programs, prev.id, { endDate: null });
  return { ...state, programs };
}

/**
 * Change the effective training max. A block without sessions yet is
 * edited in place; otherwise a new block starts (tomorrow when the exercise
 * was trained today) at the same week with the new max as its base, so the
 * old block keeps explaining the sessions done under it.
 * @param {string} id id for the new block, e.g. uid('prog')
 */
export function editTrainingMax(state, block, trainingMax, date, id) {
  const start = defaultStart(state, block.exercise, date);
  const at = programWeekIndex(block, state.logs || {}, start, state);
  if (!at.sessionsDone) return { ...state, programs: replaceBlock(state.programs, block.id, { trainingMax }) };
  const { id: _old, endDate: _end, startWeek: _sw, ...rest } = block;
  const next = createProgramBlock({ ...rest, startDate: start, trainingMax, startWeek: at.weekIndex }, id);
  return startProgram(state, next);
}

/* Sessions of a block (done sets in its dates and on its plan days) from
 * trends.sessionSeries; before `until` when given. */
function blockSessions(state, block, series, until) {
  const ids = Array.isArray(block.planDayIds) ? block.planDayIds : [];
  const plan = Array.isArray(state?.plan) ? state.plan : [];
  const filter = ids.length && ids.some(id => plan.some(d => isObj(d) && d.id === id));
  return series.filter(p => {
    if (p.date < block.startDate || (block.endDate && p.date > block.endDate) || (until && p.date >= until)) return false;
    if (!filter) return true;
    const pd = planDayFor(state, p.date);
    return !pd || !isObj(pd.day) || ids.includes(pd.day.id);
  });
}

function e1rmSpan(points) {
  const withE = points.filter(p => isNum(p.e1rm));
  return {
    sessions: points.length,
    e1rmStart: withE.length ? withE[0].e1rm : null,
    e1rmEnd: withE.length ? withE[withE.length - 1].e1rm : null,
  };
}

/**
 * The Program sheet's view of one exercise on a date.
 * @returns {{active: object|null, now: object|null, activeStats: object|null,
 *   upcoming: Array<object>, past: Array<object>}} now = programTargets of the
 *   active block; past/upcoming rows = { block, days: string[]|null, sessions,
 *   e1rmStart, e1rmEnd }, past newest first. Blocks that ended before they
 *   began (switched again the same day) are left out.
 */
export function programOverview(state, exercise, date, planDayId) {
  const plan = Array.isArray(state?.plan) ? state.plan : [];
  const logs = state?.logs || {};
  const blocks = (Array.isArray(state?.programs) ? state.programs : [])
    .filter(b => isObj(b) && b.exercise === exercise && typeof b.startDate === 'string'
      && (!b.endDate || b.endDate >= b.startDate));
  const active = activeProgram(blocks, exercise, date, { planDayId: planDayId || undefined, plan });
  const series = sessionSeries(logs, exercise, { exerciseMeta: state?.exerciseMeta });
  const row = (b, until) => ({ block: b, days: dayNames(state, b.planDayIds), ...e1rmSpan(blockSessions(state, b, series, until)) });
  const inScope = b => !planDayId || !Array.isArray(b.planDayIds) || !b.planDayIds.length
    || b.planDayIds.includes(planDayId) || !b.planDayIds.some(id => plan.some(d => isObj(d) && d.id === id));
  return {
    active,
    now: active ? programTargets(active, logs, date, state) : null,
    activeStats: active ? row(active, date) : null,
    upcoming: blocks.filter(b => b.startDate > date && inScope(b))
      .sort((a, b) => (a.startDate < b.startDate ? -1 : 1)).map(b => row(b)),
    past: blocks.filter(b => b !== active && b.startDate <= date && b.endDate && b.endDate < date)
      .sort((a, b) => (a.startDate < b.startDate ? 1 : -1)).map(b => row(b)),
  };
}
